import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AddTeamForm } from "@/pages/Add";
import CollaboratorsManager from "@/components/CollaboratorsManager";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import { baseProgramName } from "@/lib/programs";
import { ArrowLeft } from "lucide-react";

export default function TeamEditPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [team, setTeam] = useState(null);
    const [rosters, setRosters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [denied, setDenied] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            const t = await api.get(`/teams/${id}`).then((r) => r.data).catch(() => null);
            const prog = await api.get(`/teams/${id}/program`).then((r) => r.data).catch(() => []);
            if (!alive) return;
            if (!t) { setLoading(false); return; }
            const canEdit = !!(user && (user.is_admin || t.owner_user_id === user.id));
            if (!canEdit) { setDenied(true); setLoading(false); return; }
            setTeam(t);
            setRosters(prog && prog.length ? prog : [t]);
            setLoading(false);
        })();
        return () => { alive = false; };
    }, [id, user]);

    if (loading) return <div className="w-full px-6 py-20 text-center text-[#a0a0a0]" data-testid="team-edit-loading">Loading…</div>;
    if (denied) return <div className="w-full px-6 py-20 text-center text-[#a0a0a0]" data-testid="team-edit-denied">You don't have permission to edit this listing.</div>;
    if (!team) return <div className="w-full px-6 py-20 text-center text-[#a0a0a0]">Listing not found.</div>;

    const programName = baseProgramName(team) || team.name;

    return (
        <div className="w-full px-4 sm:px-6 lg:px-10 py-8 max-w-4xl mx-auto" data-testid="team-edit-page">
            <button onClick={() => window.close()} data-testid="edit-close-btn" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500] transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" /> Close
            </button>
            <h1 className="font-display text-3xl md:text-4xl tracking-wide mt-4 mb-2">Edit — {programName}</h1>
            <p className="text-sm text-[#a0a0a0] mb-6">Owner editor. Update your program details, each grade/age team's coach &amp; contacts, photos, and moderators.</p>

            <EmailVerifyBanner />

            <AddTeamForm
                sport={team.sport}
                existing={{ base_name: programName, rosters }}
                onDone={() => navigate(`/teams/${team.id}`)}
            />

            <div className="mt-10 border-t border-[#2a2a2a] pt-8" data-testid="edit-moderators-section">
                <h2 className="font-display text-2xl tracking-wide mb-1">Invite Moderators</h2>
                <p className="text-sm text-[#a0a0a0] mb-4">Give trusted people permission to help edit and moderate this program.</p>
                <CollaboratorsManager entityType="team" entityId={team.id} accent="#ff4500" canManage />
            </div>
        </div>
    );
}
